import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { useCommon } from 'src/domains/common/Common';
import { UniverseComponent } from 'src/domains/common/universes';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { Button } from 'src/domains/layouts/webview/components/common/button/Button';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { LanguageTokenType } from 'src/domains/layouts/state/languagesState/LanguagesUtils';
import { ErrorCircleIcon } from 'src/domains/players/state/IdMission/IdMissionPopup/assets/ErrorCircleIcon';
import {
    FailureOverlayWrapper,
    FailureOverlayLogo,
    FailureOverlayIcon,
    FailureOverlayText,
    FailureOverlayParagraph,
    FailureOverlayContactLink,
    FailureOverlayButtonWrapper,
} from './FailureOverlay.style';

interface FailureOverlayProps {
    onClose: () => void;
    failedReason: string;
}

interface ContactLink {
    href: string;
    text: string;
}

const createTokenMapper =
    (tokenLinks: Record<string, ContactLink>) =>
    (token: LanguageTokenType): React.ReactNode => {
        const link = tokenLinks[token.tag];
        if (link !== undefined) {
            return (
                <FailureOverlayContactLink
                    key={token.tag}
                    href={link.href}
                >
                    {link.text}
                </FailureOverlayContactLink>
            );
        }
        return null;
    };

export const FailureOverlay = observer('FailureOverlay', ({ onClose, failedReason }: FailureOverlayProps) => {
    const common = useCommon();
    const config = ConfigComponents.get(common).config;
    const languagesState = LanguagesState.get(common);
    const accountHelperMail = config.accountHelperMail;
    const identityPhoneContact = config.identityPhoneContact;

    const tokenLinks: Record<string, ContactLink> = {
        accountHelperMail: { href: `mailto:${accountHelperMail}`, text: accountHelperMail },
        ...(identityPhoneContact !== null && {
            identityPhoneContact: { href: `tel:${identityPhoneContact}`, text: identityPhoneContact },
        }),
    };

    const tokenMapper = createTokenMapper(tokenLinks);

    return (
        <FailureOverlayWrapper data-test='id-mission-failure-overlay'>
            <FailureOverlayLogo>
                <UniverseComponent>
                    {(universeVersion): React.ReactElement => <universeVersion.headerLogo />}
                </UniverseComponent>
            </FailureOverlayLogo>
            <FailureOverlayIcon>
                <ErrorCircleIcon />
            </FailureOverlayIcon>
            <FailureOverlayText>
                <FailureOverlayParagraph>
                    <I18n
                        langKey='id-mission.failure.text-1'
                        defaultText='Unfortunately, we are unable to verify your documents at this time due to:'
                    />
                </FailureOverlayParagraph>
                <FailureOverlayParagraph>{failedReason}</FailureOverlayParagraph>
                <FailureOverlayParagraph>
                    {languagesState.translateTokens(
                        languagesState.getTranslation(
                            'id-mission.failure.text-2',
                            'To activate your account, please upload and email clear, valid documents to [accountHelperMail] so we can complete the process smoothly.'
                        ),
                        tokenMapper
                    )}
                </FailureOverlayParagraph>
                <FailureOverlayParagraph>
                    <I18n
                        langKey='id-mission.failure.text-suspended'
                        defaultText='Unfortunately, your account is currently suspended and will remain suspended unless we receive the required documents.'
                    />
                </FailureOverlayParagraph>
                {identityPhoneContact !== null && (
                    <FailureOverlayParagraph>
                        {languagesState.translateTokens(
                            languagesState.getTranslation(
                                'id-mission.failure.text-4',
                                'If you need assistance, please get in touch with us on: [identityPhoneContact]'
                            ),
                            tokenMapper
                        )}
                    </FailureOverlayParagraph>
                )}
            </FailureOverlayText>
            <FailureOverlayButtonWrapper>
                <Button
                    size='medium'
                    onClick={onClose}
                >
                    <I18n
                        langKey='id-mission.failure.button'
                        defaultText='Confirm'
                    />
                </Button>
            </FailureOverlayButtonWrapper>
        </FailureOverlayWrapper>
    );
});
